import clsx from 'clsx';
import { StaticImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';

import Link from 'components/shared/link';

import dashboardMobileSvg from './images/dashboard-mobile.svg';
import dashboardSvg from './images/dashboard.svg';
import managementMobileSvg from './images/management-mobile.svg';
import managementSvg from './images/management.svg';
import noCodeMobileSvg from './images/no-code-mobile.svg';
import noCodeSvg from './images/no-code.svg';

const icons = {
  'customer-management': {
    imageWrapperClassName:
      'right-0 bottom-0 xl:object-contain xl:-bottom-2 xl:-right-16 lg:-bottom-3 lg:-right-3 md:-bottom-0 sm:object-scale-down sm:bottom-16 sm:-right-[171px] sm:h-[246px] sm:w-[518px]',
    image: {
      origin: {
        src: managementSvg,
        width: 728,
        height: 280,
      },
      mobile: {
        src: managementMobileSvg,
        width: 523,
        height: 326,
      },
    },
  },
  'no-code': {
    imageWrapperClassName:
      'right-14 bottom-5 scale-[1.18] object-scale-down xl:right-16 xl:-bottom-4 lg:right-24 md:right-4 md:bottom-6 sm:scale-100 sm:right-0 sm:bottom-0',
    image: {
      origin: {
        src: noCodeSvg,
        width: 611,
        height: 280,
      },
      mobile: {
        src: noCodeMobileSvg,
        width: 328,
        height: 421,
      },
    },
  },
  analytics: {
    imageWrapperClassName:
      'right-0 bottom-0 xl:-right-10 xl:-bottom-2 lg:bottom-0 lg:-right-3 md:h-[243px] md:bottom-8 sm:w-[500px] sm:-right-36 sm:bottom-14',
    image: {
      origin: {
        src: dashboardSvg,
        width: 728,
        height: 280,
      },
      mobile: {
        src: dashboardMobileSvg,
        width: 522,
        height: 287,
      },
    },
  },
};

const Benefits = ({ title, description, items }) => (
  <section className="benefits safe-paddings overflow-hidden">
    <div className="container">
      <h2 className="text-new-black text-[56px] leading-denser font-medium tracking-tight-4 text-center xl:text-[48px] lg:text-[36px] md:text-[32px]">
        {title}
      </h2>
      <p className="mt-5 text-xl leading-snug font-light text-new-gray-1 text-center max-w-[592px] mx-auto xl:mt-[18px] xl:text-[20px] xl:max-w-[516px] lg:mt-3.5 lg:text-[18px] md:text-[16px] lg:max-w-[478px] md:mt-3 md:max-w-80">
        {description}
      </p>
      <div className="grid grid-cols-12 grid-gap-x mt-16 xl:mt-12 lg:mt-9 lg:flex lg:flex-col md:mt-[22px]">
        <div className="col-span-5 -ml-4 -mt-2 w-[475px] xl:w-[403px] lg:hidden">
          <StaticImage
            src="./images/logos.jpg"
            alt="Schema shows the connection where data from all platforms (Apple, Android, Stripe, Roku and Web) is accessible in RevenueCat for analysis and customer management"
            width={475}
            height={930}
          />
        </div>
        <div className="col-span-5 hidden lg:flex lg:justify-center xs:hidden">
          <StaticImage
            className="-mx-4 -mt-1 max-w-[741px] md:md:w-full"
            src="./images/logos-sm.jpg"
            alt="Schema shows the connection where data from all platforms (Apple, Android, Stripe, Roku and Web) is accessible in RevenueCat for analysis and customer management"
            width={741}
            height={297}
          />
        </div>
        <div className="col-span-5 hidden xs:flex justify-center">
          <StaticImage
            className="max-w-[342px]"
            src="./images/logos-mobile.jpg"
            alt="Schema shows the connection where data from all platforms (Apple, Android, Stripe, Roku and Web) is accessible in RevenueCat for analysis and customer management"
            width={342}
            height={162}
          />
        </div>
        <ul className="relative col-span-7 -ml-8 col-end-13 grid grid-gap auto-rows-[280px] xl:-ml-6 xl:auto-rows-[240px] lg:ml-0 lg:mt-11 lg:gap-12 md:gap-5 md:mt-2 md:auto-rows-[431px]">
          {items.map(({ title, description, iconName, linkText, linkUrl }, index) => {
            const { image, imageWrapperClassName } = icons[iconName];

            return (
              <li
                className="relative rounded-2xl border bg-[linear-gradient(180deg,#FCFCFD_98.67%,#f0f0ff11)] border-[#EAEDF6] overflow-hidden lg:overflow-visible xl:w-[574px] xl:h-60 lg:w-full lg:before:absolute lg:before:-top-[38px] lg:before:left-1/2 lg:before:h-[30px] lg:before:-translate-x-px lg:before:w-[3px] lg:before:bg-line md:before:hidden md:h-[431px]"
                key={index}
              >
                <div className="h-full w-full lg:relative rounded-2xl lg:overflow-hidden">
                  <div
                    className={clsx(
                      'absolute pointer-events-none sm:hidden',
                      imageWrapperClassName
                    )}
                  >
                    <img
                      src={image.origin.src}
                      alt={title}
                      width={image.origin.width}
                      height={image.origin.height}
                      loading="lazy"
                    />
                  </div>
                  <div
                    className={clsx(
                      'absolute pointer-events-none hidden sm:block',
                      imageWrapperClassName
                    )}
                  >
                    <img
                      src={image.mobile.src}
                      alt={title}
                      width={image.mobile.width}
                      height={image.mobile.height}
                      loading="lazy"
                    />
                  </div>

                  <div className="flex flex-col pt-8 pl-8 xl:pt-7 xl:pl-7 md:pl-5 md:pt-5 z-10 max-w-[360px] xl:max-w-[332px] lg:max-w-[358px]">
                    <h3 className="text-[24px] leading-snug font-medium text-new-black tracking-tight-4 xl:text-[20px] lg:text-[18px]">
                      {title}
                    </h3>
                    <p className="mt-2 text-base leading-snug font-light text-new-gray-1 xl:max-w-[284px] lg:text-[14px] lg:max-w-[332px] md:max-w-72">
                      {description}
                    </p>
                    <Link
                      className="mt-7 xl:mt-[18px] xl:text-[16px] lg:text-[14px] md:mt-3"
                      to={linkUrl}
                      theme="secondary-2"
                      size="md"
                      withArrow
                    >
                      {linkText}
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  </section>
);

Benefits.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      iconName: PropTypes.oneOf(Object.keys(icons)).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default Benefits;
